import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

// آماده کردنِ فایل‌های «ارسال فایل روی سرور» روی خودِ گوشی:
//   encodeLightmap   نقشه‌ی نورِ پخته‌شده (HDR، روی کارت گرافیک) → JPEG + ضریبِ روشنایی
//   exportBakedGlb   ساختمانِ پخته‌شده → GLB (هندسه + جنس‌ها + UVِ دومِ نقشه‌ی نور)
//   compressBlob     فشرده‌سازیِ gzip (اگر مرورگر داشته باشد)
//   FrameCapture     عکسِ کوچک از همان چیزی که کاربر الان می‌بیند
//
// قالبِ خروجی دقیقاً همان قالبِ رندرِ سرور است (result.glb + lightmap.jpg + lightmapIntensity)، پس یک
// نمایش‌دهنده (BakedModel) هر دو را نشان می‌دهد.

// ---------- نقشه‌ی نور ----------
const ENCODE_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const ENCODE_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uSrc;
uniform float uScale;
varying vec2 vUv;
vec3 toSrgb(vec3 c) {
  c = clamp(c, 0.0, 1.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
}
void main() { gl_FragColor = vec4(toSrgb(texture2D(uSrc, vUv).rgb / uScale), 1.0); }
`;

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

// نقشه‌ی نور را با ضریبِ scale به تصویرِ ۸ بیتیِ sRGB تبدیل می‌کند و پیکسل‌ها را برمی‌گرداند. از خواندنِ
// مستقیمِ تصویرِ HDR (که بعضی گوشی‌ها اجازه نمی‌دهند) استفاده نمی‌شود — همیشه از یک تصویرِ معمولی خوانده می‌شود
function renderEncoded(renderer, srcTexture, size, scale) {
  const rt = new THREE.WebGLRenderTarget(size, size, {
    type: THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
  });
  const material = new THREE.ShaderMaterial({
    vertexShader: ENCODE_VERT,
    fragmentShader: ENCODE_FRAG,
    uniforms: { uSrc: { value: srcTexture }, uScale: { value: scale } },
    depthTest: false,
    depthWrite: false,
  });
  const geo = new THREE.PlaneGeometry(2, 2);
  const quad = new THREE.Mesh(geo, material);
  quad.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(quad);
  const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const prevTarget = renderer.getRenderTarget();
  const prevScissor = renderer.getScissorTest();
  const prevAutoClear = renderer.autoClear;
  const pixels = new Uint8Array(size * size * 4);
  try {
    renderer.setScissorTest(false);
    renderer.autoClear = true;
    renderer.setRenderTarget(rt);
    renderer.render(scene, cam);
    renderer.readRenderTargetPixels(rt, 0, 0, size, size, pixels);
  } finally {
    renderer.setRenderTarget(prevTarget);
    renderer.setScissorTest(prevScissor);
    renderer.autoClear = prevAutoClear;
    rt.dispose();
    material.dispose();
    geo.dispose();
  }
  return pixels;
}

// روشنیِ «تقریباً بیشینه» (صدکِ ۹۹٫۵) — چند نقطه‌ی خیلی روشن (لامپ، آفتابِ مستقیم) کلِ تصویر را تاریک نکنند
function brightPercentile(pixels, scale, q = 0.995) {
  const lin = new Float32Array(256);
  for (let i = 0; i < 256; i += 1) lin[i] = srgbToLinear(i / 255) * scale;
  const vals = [];
  for (let i = 0; i < pixels.length; i += 4) {
    const m = Math.max(pixels[i], pixels[i + 1], pixels[i + 2]);
    if (m > 0) vals.push(lin[m]);
  }
  if (!vals.length) return 1;
  vals.sort((a, b) => a - b);
  return vals[Math.min(vals.length - 1, Math.floor(vals.length * q))];
}

async function pixelsToJpeg(pixels, size, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  // سطرِ صفر = v=0؛ تصویرهای glTF هم بدونِ برگرداندن (flipY=false) خوانده می‌شوند — پس همان ترتیب
  ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels.buffer), size, size), 0, 0);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  canvas.width = 1;
  canvas.height = 1;
  if (!blob) throw new Error('lightmap-encode-failed');
  return blob;
}

// → { blob (JPEG)، intensity } — intensity همان meta.lightmapIntensity رندرِ سرور است (نمایش: × π)
export async function encodeLightmap(renderer, lightMap, size) {
  // ۱) نسخه‌ی کوچک با ضریبِ بزرگ فقط برای پیدا کردنِ روشنیِ مناسب
  const probeScale = 32;
  const probe = renderEncoded(renderer, lightMap, 512, probeScale);
  const scale = Math.max(1e-3, brightPercentile(probe, probeScale));
  // ۲) نسخه‌ی کامل
  const pixels = renderEncoded(renderer, lightMap, size, scale);
  const blob = await pixelsToJpeg(pixels, size, 0.92);
  return { blob, intensity: scale / Math.PI };
}

// ---------- مدل ----------
export function exportBakedGlb(root) {
  // چراغ‌ها جزوِ فایل نباشند (نور قبلاً پخته شده)
  const lights = [];
  root.traverse((o) => {
    if (o.isLight && o.parent) lights.push({ o, parent: o.parent });
  });
  lights.forEach(({ o, parent }) => parent.remove(o));
  const restore = () => lights.forEach(({ o, parent }) => parent.add(o));
  return new Promise((resolve, reject) => {
    try {
      new GLTFExporter().parse(
        root,
        (result) => {
          restore();
          resolve(result);
        },
        (error) => {
          restore();
          reject(error);
        },
        // onlyVisible: false — سقف (در نمای پیش‌فرض مخفی) و لنگه‌های در/پنجره هم در فایل باشند
        { binary: true, onlyVisible: false, maxTextureSize: 2048 }
      );
    } catch (e) {
      restore();
      reject(e);
    }
  });
}

// → { blob, gzip } — اگر مرورگر فشرده‌سازی نداشت (iOS قدیمی)، همان فایلِ خام
export async function compressBlob(data, type = 'application/octet-stream') {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  if (typeof CompressionStream === 'undefined') return { blob, gzip: false };
  try {
    const stream = blob.stream().pipeThrough(new CompressionStream('gzip'));
    const gz = await new Response(stream).blob();
    return { blob: gz, gzip: true };
  } catch (e) {
    return { blob, gzip: false };
  }
}

// ---------- عکسِ کوچک ----------
// داخلِ <Canvas>: captureRef.current(maxWidth) → Blob (JPEG)
export function FrameCapture({ captureRef }) {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    captureRef.current = (maxWidth = 960) => {
      gl.render(scene, camera); // همین حالا بکش و بلافاصله کپی کن (بدونِ نگه داشتنِ تصویر در حافظه‌ی کارت)
      const src = gl.domElement;
      const s = Math.min(1, maxWidth / src.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(src.width * s));
      canvas.height = Math.max(1, Math.round(src.height * s));
      canvas.getContext('2d').drawImage(src, 0, 0, canvas.width, canvas.height);
      return new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('thumb-failed'))), 'image/jpeg', 0.85);
      });
    };
    return () => {
      captureRef.current = null;
    };
  }, [gl, scene, camera, captureRef]);
  return null;
}

// عکسِ آگهی (آدرسِ موقتِ blob: یا هر آدرسِ عکس) → JPEG با بلندترین ضلعِ حداکثر maxSize
export async function compressPhoto(url, maxSize = 2048, quality = 0.85) {
  const src = await fetch(url).then((r) => {
    if (!r.ok) throw new Error('photo-missing');
    return r.blob();
  });
  const bitmap = await createImageBitmap(src);
  const s = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * s));
  canvas.height = Math.max(1, Math.round(bitmap.height * s));
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  if (bitmap.close) bitmap.close();
  const out = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  canvas.width = 1;
  canvas.height = 1;
  if (!out) throw new Error('photo-encode-failed');
  return out;
}
